import { useEffect, useRef, useState, type CSSProperties } from 'react'

import { useNetzbild } from '../hooks/useNetzbild'

/**
 * A picture from the server -- **the one component for it** (1.78.0).
 *
 * It asks again in two situations, and until 1.77.0 they were two components
 * and a third way that asked never:
 *
 * - **It is not there yet** (1.18.0, from use): a video's poster goes up
 *   **after** the video (`uploadVideo()` waits a second and a half and then
 *   lets it follow). A card drawn in that gap asks `/poster`, gets a 404 --
 *   and a plain `<img>` never asks again. So after a failure it waits,
 *   doubles the wait, and tries once more with a fresh address (`?v=n`, so
 *   that no cache answers for the server), six times in all -- about a
 *   minute.
 * - **The net was gone** (`useNetzbild`, 1.50.0): an `<img>` that failed
 *   while offline keeps its address, and an address that does not change is
 *   never requested again. Here only `Netzbild` asked again; the photos in
 *   the chat were a plain `<img>` and kept the broken sign, the map cards
 *   were `Standbild` and gave up after the minute and stayed empty. From the
 *   check of 1.78.0: open the app offline, the net comes back, the posts
 *   arrive -- and their photos do not.
 *
 * Both counters go into the address; a net that returns starts the waiting
 * over.
 *
 * **What stands there when it gave up is a surface, not a gap.** Until here
 * the picture became `visibility: hidden`, and on a card that left the bare
 * card background -- not the surface "without photo" a point without a photo
 * has. It is now a `<span>` with the same class and `standbild--fehlt`,
 * which the stylesheet fills.
 */
const WARTEN_MS = [1000, 2000, 4000, 8000, 16000, 16000]

export function Standbild({
  src,
  className,
  loading,
  style,
  width,
  height,
}: {
  src: string
  className?: string
  loading?: 'lazy' | 'eager'
  style?: CSSProperties
  width?: number
  height?: number
}) {
  const element = useRef<HTMLImageElement>(null)
  const netz = useNetzbild(
    src,
    () => element.current !== null && element.current.complete && element.current.naturalWidth === 0,
  )
  const [versuch, setVersuch] = useState(0)
  const [aufgegeben, setAufgegeben] = useState(false)
  /*
   * **Between a failure and the next success the picture is hidden**
   * (1.90.0). From use, with a screenshot: the app started without a net,
   * and every tile showed iOS' sign for a broken picture -- a blue question
   * mark -- for the minute until the asking gave up. The tile's own surface
   * stands behind it; that is what "not there yet" should look like.
   */
  const [stolpert, setStolpert] = useState(false)
  const uhr = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // A new address, or the net back: start over. The timer of the old one
  // goes with it -- it would only ask once more for nothing.
  useEffect(() => {
    clearTimeout(uhr.current)
    setVersuch(0)
    setAufgegeben(false)
  }, [src, netz.versuch])
  useEffect(() => () => clearTimeout(uhr.current), [])

  function fehlgeschlagen() {
    setStolpert(true)
    netz.fehlgeschlagen()
    const warten = WARTEN_MS[versuch]
    if (warten === undefined) {
      setAufgegeben(true)
      return
    }
    clearTimeout(uhr.current)
    uhr.current = setTimeout(() => setVersuch((n) => n + 1), warten)
  }

  const basis = netz.adresse ?? src
  const adresse = versuch === 0 ? basis : `${basis}${basis.includes('?') ? '&' : '?'}w=${versuch}`

  if (aufgegeben) {
    return (
      <span
        className={`${className ?? ''} standbild--fehlt`.trim()}
        style={style}
        aria-hidden="true"
        data-standbild="fehlt"
      />
    )
  }
  return (
    <img
      key={adresse}
      ref={element}
      className={stolpert ? `${className ?? ''} standbild--stolpert`.trim() : className}
      style={style}
      src={adresse}
      alt=""
      loading={loading}
      width={width}
      height={height}
      onError={fehlgeschlagen}
      onLoad={() => setStolpert(false)}
      data-standbild-versuch={versuch}
      data-netzbild-versuch={netz.versuch}
    />
  )
}
